import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { ManagementOrganizationSearchModel } from '../models/management/management-organization-search-model';
import { PaginatedResultModel } from '../models/common';
import { OrganizationListManagementModel } from '../models/management/management-organization-list-model';
import { OrganizationDetailedManagementModel } from '../models/management/management-organization-detailed-model';
import { UpdateOrganizationPaymentTypeManagementModel } from '../models/management/management-organization-update-payment-type-model';
import { OrganizationMemberInvitationModel } from '../models/organizations';
import { UpdateIsFilingRestrictedManagementModel, UpdateOrganizationSupportSettingsManagementModel } from '../models/management';
import { SpecialRateModel } from '../models/common/special-rate-model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsManagementRepository {
  constructor(private apiService: ApiService) {}

  public getOrganization(id: number, isShowLoader: boolean = true): Observable<OrganizationDetailedManagementModel> {
    return this.apiService.get<OrganizationDetailedManagementModel>(`${environment.url.organizationsManagement}${id}`, null, {
      isShowLoader,
    });
  }

  public searchOrganizations(
    params?: ManagementOrganizationSearchModel,
    isShowLoader: boolean = true,
  ): Observable<PaginatedResultModel<OrganizationListManagementModel>> {
    return this.apiService.get<PaginatedResultModel<OrganizationListManagementModel>>(
      `${environment.url.organizationsManagement}`,
      {
        params,
      },
      { isShowLoader },
    );
  }

  public getOrganizationInvitedMembers(id: number, isShowLoader: boolean = true): Observable<OrganizationMemberInvitationModel[]> {
    return this.apiService.get<OrganizationMemberInvitationModel[]>(
      `${environment.url.organizationsManagement}${id}/invited-members`,
      null,
      {
        isShowLoader,
      },
    );
  }

  public setOrganizationIsCollectionFee(
    organizationId: number,
    params: UpdateOrganizationPaymentTypeManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/is-collection-fee`, params, null, {
      isShowLoader,
    });
  }

  public setOrganizationIsInstantDutiesPayment(
    organizationId: number,
    params: UpdateOrganizationPaymentTypeManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(
      `${environment.url.organizationsManagement}${organizationId}/is-instant-duties-payment`,
      params,
      null,
      { isShowLoader },
    );
  }

  public setOrganizationIsDutiesConfirmation(
    organizationId: number,
    params: UpdateOrganizationPaymentTypeManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/is-duties-confirmation`, params, null, {
      isShowLoader,
    });
  }

  public setOrganizationPaymentType(
    organizationId: number,
    params: UpdateOrganizationPaymentTypeManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/payment-type`, params, null, {
      isShowLoader,
    });
  }

  public setOrganizationSapCardCode(organizationId: number, sapCardCode: string, isShowLoader: boolean = false): Observable<void> {
    return this.apiService.put<void>(
      `${environment.url.organizationsManagement}${organizationId}/sap-card-code`,
      `"${sapCardCode}"`,
      null,
      { isShowLoader },
    );
  }

  public setOrganizationSupportSettings(
    organizationId: number,
    params: UpdateOrganizationSupportSettingsManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/support-settings`, params, null, {
      isShowLoader,
    });
  }

  public setOrganizationIsFilingRestricted(
    organizationId: number,
    params: UpdateIsFilingRestrictedManagementModel,
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/is-filing-restricted`, params, null, {
      isShowLoader,
    });
  }

  public setOrganizationBrokers(
    organizationId: number,
    params: { entrySummaryBrokerId?: number; continuousBondBrokerId?: number; truckEntrySummaryBrokerId?: number },
    isShowLoader: boolean = false,
  ): Observable<void> {
    return this.apiService.put<void>(`${environment.url.organizationsManagement}${organizationId}/brokers`, params, null, {
      isShowLoader,
    });
  }

  public getOrganizationSpecialRates(organizationId: number, isShowLoader: boolean = true): Observable<SpecialRateModel[]> {
    return this.apiService.get<SpecialRateModel[]>(`${environment.url.organizationsManagement}${organizationId}/special-rates`, null, {
      isShowLoader,
    });
  }

  public createOrganizationSpecialRate(
    organizationId: number,
    params: { validFrom: string; validTo: string; productCode: string; price: number; companyId?: number | null },
    isShowLoader: boolean = true,
  ): Observable<SpecialRateModel> {
    return this.apiService.post<SpecialRateModel>(
      `${environment.url.organizationsManagement}${organizationId}/special-rates`,
      params,
      null,
      { isShowLoader },
    );
  }

  public updateSpecialRate(
    specialRateId: number,
    params: { validFrom: string; validTo: string; productCode: string; price: number; companyId?: number | null },
    isShowLoader: boolean = true,
  ): Observable<SpecialRateModel> {
    return this.apiService.put<SpecialRateModel>(
      `${environment.url.prefixCustoms}v2/management/special-rates/${specialRateId}`,
      params,
      null,
      { isShowLoader },
    );
  }

  public deleteSpecialRate(specialRateId: number, isShowLoader: boolean = true): Observable<void> {
    return this.apiService.delete<void>(`${environment.url.prefixCustoms}v2/management/special-rates/${specialRateId}`, null, {
      isShowLoader,
    });
  }
}
